import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { catchError, firstValueFrom, of, Subscription, switchMap, takeWhile, timer } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { IFlashResetEntireLotResult } from 'src/app/shared/code/interfaces/parcs-interfaces';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';


@Component({
  selector: 'app-parcs-flash-utilities',
  standalone: true,
  imports: [
    ProcessingBannerComponent,
    MatCardModule,
    MatTabsModule
  ],
  templateUrl: './parcs-flash-utilities.component.html',
  styleUrl: './parcs-flash-utilities.component.scss'
})
export class ParcsFlashUtilitiesComponent {
  isProcessing: boolean = false;
  processingMessage: string = 'Processing...';

  private readonly RESET_ENTIRE_LOT_POLL_INTERVAL_MS = 3000;
  private resetEntireLotPollingSubscription: Subscription | null = null;

  _parcsDataService = inject(ParcsDataService);
  _appGeneralState = inject(AppGeneralStateService);
  _messageService = inject(MessageDialogService);
  _destroyRefService = inject(DestroyRef);

  // "Reset All Active Parkers" button - resets every active Parker's Flash account to neutral for
  // the current lot. Backed by ParcsController.Flash_ResetEntireLot, which can take several minutes
  // to an hour for a large lot, so the kickoff call returns immediately with a JobId and the actual
  // completion is confirmed by polling the status endpoint below rather than awaiting one long request.
  async onResetAllActiveParkers(): Promise<void> {
    this.isProcessing = true;
    this.processingMessage =
      'Resetting all Flash Parker accounts to neutral.  This may take several minutes to an hour depending on the size of the lot';

    try {
      const kickoff = await firstValueFrom(
        this._parcsDataService.flash_ResetEntireLot(this._appGeneralState.currentLotNumber),
      );

      this.pollResetEntireLotJobStatus(kickoff.jobId);
    } catch (err) {
      this.isProcessing = false;
      this._messageService.snackBarErrorTopCenterStatic('Error occurred while resetting the lot. Please try again.');
    }
  }

  private pollResetEntireLotJobStatus(jobId: string): void {
    this.stopResetEntireLotPolling();

    this.resetEntireLotPollingSubscription = timer(0, this.RESET_ENTIRE_LOT_POLL_INTERVAL_MS)
      .pipe(
        takeUntilDestroyed(this._destroyRefService),
        switchMap(() => this._parcsDataService.flash_GetResetEntireLotJobStatus(jobId)),
        takeWhile((status) => !status.isComplete, true),
        catchError(() => {
          this.isProcessing = false;
          this._messageService.snackBarErrorTopCenterStatic(
            'Error checking reset status. The reset may still be processing. Please check again shortly.',
          );
          return of(null);
        }),
      )
      .subscribe((status: IFlashResetEntireLotResult | null) => {
        if (!status || !status.isComplete) {
          return;
        }

        this.isProcessing = false;

        if (status.status === 'Completed') {
          this._messageService.snackBarTopCenterStatic(
            `Reset Complete - ${status.successCount} of ${status.totalParkersFound} Parker(s) reset successfully` +
              (status.failureCount > 0 ? `, ${status.failureCount} failed` : ''),
          );
        } else {
          this._messageService.snackBarErrorTopCenterStatic(
            status.errorMessage ? `Error resetting the lot: ${status.errorMessage}` : 'Error occurred while resetting the lot.',
          );
        }
      });
  }

  private stopResetEntireLotPolling(): void {
    this.resetEntireLotPollingSubscription?.unsubscribe();
    this.resetEntireLotPollingSubscription = null;
  }
}
